import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React from 'react'

import type { Page, Service } from '@/payload-types'

// Collections whose pages live at /${slug} (no collection prefix)
const ROOT_SLUG_COLLECTIONS = ['pages', 'services']

type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'services'
    value: Page | Service | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
  /** Forwarded to Next.js `Link` (needed for header dropdown hover on the anchor itself). */
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>
  onMouseOver?: React.MouseEventHandler<HTMLAnchorElement>
  onPointerEnter?: React.PointerEventHandler<HTMLAnchorElement>
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    onFocus,
    onMouseEnter,
    onMouseOver,
    onPointerEnter,
  } = props

  const href =
    type === 'reference' && typeof reference?.value === 'object' && reference.value.slug
      ? `${ROOT_SLUG_COLLECTIONS.includes(reference?.relationTo ?? '') ? '' : `/${reference?.relationTo}`}/${reference.value.slug}`
      : url

  if (!href) return null

  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  /* Ensure we don't break any styles set by richText */
  const linkContent = children ?? label

  const linkHoverProps = { onFocus, onMouseEnter, onMouseOver, onPointerEnter }

  if (appearance === 'inline') {
    return (
      <Link
        className={cn(className)}
        href={href || url || ''}
        {...newTabProps}
        {...linkHoverProps}
      >
        {linkContent}
      </Link>
    )
  }

  return (
    <Button asChild className={className} size={size} variant={appearance}>
      <Link className={cn(className)} href={href || url || ''} {...newTabProps} {...linkHoverProps}>
        {linkContent}
      </Link>
    </Button>
  )
}
